﻿/* https://brandplatform-20-qa.stgr.cloud/molecules/accordion */
import { expressQuerySelector, expressQuerySelectorAll, hasClass, isElementVisibleInViewPort } from "../../common/html";
import { createClickableElement } from "../atoms/button";

export interface ICreateAccordionOptions {
	readonly singleOpen?: boolean;
	readonly swapTitleDataAttribute?: string;
}

export interface ICreateAccordionDependencies {
	onInitialized?: (container: HTMLElement) => Promise<void>;
	onBeforeOpen?: (container: HTMLElement) => Promise<boolean>;
	onOpen?: (container: HTMLElement) => Promise<void>;
	readonly onBeforeClose?: (container: HTMLElement) => Promise<void>;
	readonly titleElSelector?: string;
	readonly scrollIntoViewAfterOpen: boolean;
	readonly contentSelector?: string;
}

export interface IAccordionContentRequest {
	readonly type: string;
	readonly theme: string;
	readonly removeCurrencySymbolFormUpperFunnelPages: boolean;
}

export interface IAccordion {
	dispose(): void;
	show(): void;
	close(): void;
	hide(): void;
	open(scrollIntoView?: boolean): void;
	element: HTMLElement;
	isOpen(): boolean;
}

export function createAccordion(
	container: HTMLDetailsElement,
	deps?: ICreateAccordionDependencies,
	opts?: ICreateAccordionOptions,
): IAccordion {
	const { singleOpen, swapTitleDataAttribute } = opts || {};
	const {
		onInitialized,
		onBeforeOpen,
		onBeforeClose,
		titleElSelector,
		scrollIntoViewAfterOpen,
		contentSelector,
		onOpen,
	} = deps || {};
	const titleEl = expressQuerySelector<HTMLElement>(
		container,
		titleElSelector ? titleElSelector : ".technical-accordion-title",
		true,
	);
	const contentEl = expressQuerySelector<HTMLElement>(
		container,
		contentSelector ? contentSelector : ".technical-accordion-content",
		false,
	);
	const closeWithBefore = async () => {
		if (onBeforeClose) await onBeforeClose(container);

		return close();
	};

	const openWithBefore = async (scrollIntoView = false): Promise<void> => {
		if (onBeforeOpen && !(await onBeforeOpen(container))) return;

		if (onOpen) onOpen(container);

		open(scrollIntoView);
	};

	const close = () => closeAccordion(container);
	const closeAccordion = (accordion: HTMLDetailsElement) => {
		showCloseTitle();
		accordion.removeAttribute("open");
	};
	const open = (shouldScrollIntoView?: boolean) => {
		if (singleOpen) {
			const collapsibleEls = expressQuerySelectorAll<HTMLDetailsElement>(
				container.parentElement,
				".technical-accordion",
			);
			collapsibleEls.forEach((el) => closeAccordion(el));
		}

		showOpenTitle();
		container.open = true;

		if ((scrollIntoViewAfterOpen && !isElementVisibleInViewPort(contentEl)) || shouldScrollIntoView)
			titleEl.scrollIntoView({ behavior: "smooth", block: "start" });
	};

	const isOpen = () => container.open;
	const onClick = () => (isOpen() ? closeWithBefore() : openWithBefore());

	const button = createClickableElement(titleEl, onClick);

	const showOpenTitle = () => {
		if (!swapTitleDataAttribute) return;
		if (titleEl.innerText === container.dataset.accTitleWhenOpen) return;

		titleEl.innerText = container.dataset.accTitleWhenOpen;
	};

	const showCloseTitle = () => {
		if (!swapTitleDataAttribute) return;
		if (titleEl.innerText == container.dataset.accTitleWhenClosed) return;

		titleEl.innerText = container.dataset.accTitleWhenClosed;
	};

	if (swapTitleDataAttribute) {
		const isCurrentlyOpen = isOpen();
		container.dataset.accTitleWhenOpen = isCurrentlyOpen
			? titleEl.innerText
			: container.getAttribute(swapTitleDataAttribute);
		container.dataset.accTitleWhenClosed = isCurrentlyOpen
			? container.getAttribute(swapTitleDataAttribute)
			: titleEl.innerText;
	}

	onInitialized && onInitialized(container);

	return {
		dispose: button.dispose,
		show: () => container.classList.remove("u-hide"),
		hide: () => container.classList.add("u-hide"),
		close,
		open: (scrollIntoView?: boolean) => openWithBefore(scrollIntoView),
		element: container,
		isOpen,
	} as IAccordion;
}
